import { useEffect, useState } from 'react';
import { Loader2, FileText, Download, CreditCard, Repeat } from 'lucide-react';
import { toast } from 'sonner';
import { api, API } from '@/App';
import AdminLayout from '@/components/AdminLayout';

const LEAD_STATUSES = ['new', 'contacted', 'quoted', 'closed', 'lost'];

export default function AdminCrm() {
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [tab, setTab] = useState('pipeline');
  const [stmtMonth, setStmtMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busyProposal, setBusyProposal] = useState('');

  const load = () => {
    api.get('/sales/my').then((r) => setStats(r.data)).catch(() => setStats(null));
    api.get('/sales/customers').then((r) => setCustomers(r.data)).catch(() => setCustomers([]));
  };
  useEffect(() => { load(); }, []);

  const setLeadStatus = async (kind, id, status) => {
    try {
      await api.patch(`/leads/${kind}/${id}`, { status });
      setStats((s) => ({ ...s, leads: s.leads.map((l) => (l.id === id && l.kind === kind ? { ...l, lead_status: status } : l)) }));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update the lead.'); }
  };

  const makeProposal = async (id) => {
    setBusyProposal(id);
    try {
      const { data } = await api.post(`/proposals/${id}`);
      toast.success('Proposal drafted — downloading PDF.');
      const res = await api.get(`/proposals/${data.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `gslab-proposal-${data.id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not draft the proposal.'); }
    finally { setBusyProposal(''); }
  };

  const pct = stats && stats.target_sales > 0 ? Math.min(100, (stats.sales_count / stats.target_sales) * 100) : 0;
  const refLink = stats?.sales_code ? `${window.location.origin}/?ref=${stats.sales_code}` : '';

  return (
    <AdminLayout title="Sales CRM" subtitle="Pipeline, paying customers, referral tracking and AI proposal drafts." testId="admin-crm-page">
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="border border-slate-800 rounded-xl bg-[#0B1528]/70 px-4 py-4">
            <div className="font-display font-extrabold text-2xl text-emerald-300">{stats.sales_count}</div>
            <div className="font-mono-tech text-[9px] tracking-[0.2em] text-slate-500 mt-1">SALES MADE</div>
          </div>
          <div className="border border-slate-800 rounded-xl bg-[#0B1528]/70 px-4 py-4">
            <div className="font-display font-extrabold text-2xl text-amber-300">${Number(stats.sales_total).toLocaleString()}</div>
            <div className="font-mono-tech text-[9px] tracking-[0.2em] text-slate-500 mt-1">REVENUE CLOSED</div>
          </div>
          <div className="border border-slate-800 rounded-xl bg-[#0B1528]/70 px-4 py-4">
            <div className="font-display font-extrabold text-2xl text-slate-200">{stats.target_sales || '—'}</div>
            <div className="font-mono-tech text-[9px] tracking-[0.2em] text-slate-500 mt-1">MONTHLY TARGET</div>
          </div>
          <div className="border border-emerald-500/25 rounded-xl bg-emerald-500/5 px-4 py-4">
            <div className="font-display font-extrabold text-2xl text-emerald-300" data-testid="commission-earned">
              ${Number(stats.commission_owed).toLocaleString()}
            </div>
            <div className="font-mono-tech text-[9px] tracking-[0.2em] text-slate-500 mt-1">
              COMMISSION ({stats.commission_pct || 0}%){stats.commission_paid ? ' · PAID' : ''}
            </div>
          </div>
        </div>
      )}

      {refLink && (
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span className="text-xs text-slate-500">Referral link:</span>
          <code data-testid="sales-ref-link" className="font-mono-tech text-xs text-emerald-300 bg-emerald-500/5 border border-emerald-500/25 rounded-lg px-3 py-2">{refLink}</code>
          <button data-testid="sales-ref-copy-button" onClick={() => { navigator.clipboard.writeText(refLink); toast.success('Referral link copied.'); }}
            className="text-xs border border-slate-600 hover:border-emerald-400/60 text-slate-300 rounded-full px-4 py-2 transition-colors">Copy</button>
        </div>
      )}
      {stats && stats.target_sales > 0 && (
        <div className="mb-8">
          <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300 transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
          <p className="font-mono-tech text-[10px] tracking-widest text-slate-500 mt-2">{stats.sales_count} / {stats.target_sales} TO TARGET</p>
        </div>
      )}

      <div className="flex items-center gap-2 mb-6">
        {[['pipeline', 'Lead Pipeline'], ['customers', 'Customers']].map(([v, l]) => (
          <button key={v} data-testid={`crm-tab-${v}`} onClick={() => setTab(v)}
            className={`font-mono-tech text-[11px] tracking-widest uppercase rounded-full px-4 py-2 transition-colors ${tab === v ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40' : 'text-slate-400 border border-slate-700/60 hover:text-amber-200'}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'pipeline' && (
        <div className="space-y-2 mb-10">
          {!stats ? (
            <div className="flex justify-center py-10"><Loader2 size={20} className="text-amber-400 animate-spin" /></div>
          ) : (stats.leads || []).length === 0 ? (
            <p className="text-sm text-slate-500 border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-8 text-center">No assigned leads yet — enquiries and bookings land here.</p>
          ) : stats.leads.map((l, i) => (
            <div key={i} data-testid="sales-lead-row" className="flex flex-wrap items-center justify-between gap-3 border border-slate-800 rounded-xl bg-[#0F1C33]/60 px-5 py-4">
              <div className="min-w-0">
                <div className="text-sm text-slate-200">{l.name} <span className="text-slate-500">· {l.email}</span></div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {l.kind === 'booking' ? `Consultation ${l.date} ${l.time} ET` : (l.business || l.message?.slice(0, 60))}
                  {l.source === 'ai_chat' && <span className="ml-2 text-amber-300/80">via AI chat</span>}
                </div>
                {l.ai_summary && <div className="text-xs text-amber-200/70 mt-1.5 border-l-2 border-amber-500/40 pl-2 whitespace-pre-wrap">{l.ai_summary}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {l.kind === 'enquiry' && (
                  <button data-testid="proposal-generate-button" onClick={() => makeProposal(l.id)} disabled={busyProposal === l.id}
                    className="inline-flex items-center gap-1.5 text-[11px] border border-amber-500/40 text-amber-300 rounded-full px-3 py-1.5 hover:bg-amber-500/10 transition-colors disabled:opacity-40">
                    {busyProposal === l.id ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />} Proposal PDF
                  </button>
                )}
                <span className={`font-mono-tech text-[9px] tracking-widest border rounded-full px-3 py-1 ${l.kind === 'booking' ? 'text-sky-300 border-sky-500/40' : 'text-amber-300 border-amber-500/40'}`}>
                  {l.kind === 'booking' ? 'BOOKING' : 'ENQUIRY'}
                </span>
                <select data-testid="lead-status-select" value={l.lead_status || 'new'} onChange={(e) => setLeadStatus(l.kind, l.id, e.target.value)}
                  className="bg-[#0B1528] border border-slate-700 rounded-lg text-[11px] px-2.5 py-1.5 text-slate-200 outline-none">
                  {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'customers' && (
        <div className="space-y-3 mb-10">
          {customers === null ? (
            <div className="flex justify-center py-10"><Loader2 size={20} className="text-amber-400 animate-spin" /></div>
          ) : customers.length === 0 ? (
            <p className="text-sm text-slate-500 border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-8 text-center">No paying customers yet — they'll appear here the moment the first checkout completes.</p>
          ) : customers.map((c) => (
            <div key={c.email} data-testid="crm-customer-row" className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <div className="text-sm text-slate-200">{c.name || c.email} {c.name && <span className="text-slate-500">· {c.email}</span>}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Customer since {c.since ? new Date(c.since).toLocaleDateString() : '—'}
                    {c.project && ` · Project: ${c.project.title} (${c.project.status.replace('_', ' ')}, ${c.project.milestones_done}/${c.project.milestones_total} milestones)`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {c.monthly && (
                    <span className="inline-flex items-center gap-1.5 font-mono-tech text-[9px] tracking-widest border rounded-full px-3 py-1 text-sky-300 border-sky-500/40">
                      <Repeat size={10} /> MONTHLY PLAN
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5 font-mono-tech text-[9px] tracking-widest border rounded-full px-3 py-1 text-emerald-300 border-emerald-500/40">
                    <CreditCard size={10} /> ${Number(c.total).toLocaleString()} LIFETIME
                  </span>
                </div>
              </div>
              <div className="space-y-1.5">
                {c.plans.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-xs border border-slate-800/70 rounded-lg bg-[#0B1528]/70 px-3 py-2">
                    <span className="text-slate-300">{p.package_name}</span>
                    <span className="font-mono-tech text-slate-400">
                      {p.kind === 'subscription' ? 'MONTHLY' : 'ONE-TIME'} · ${Number(p.amount).toLocaleString()}
                      {p.sales_code && <span className="text-emerald-400/70 ml-2">{p.sales_code}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {stats && (
        <div className="flex flex-wrap items-center gap-3 border-t border-slate-800/60 pt-6">
          <span className="text-xs text-slate-500">Commission statement:</span>
          <input data-testid="statement-month-input" type="month" value={stmtMonth} onChange={(e) => setStmtMonth(e.target.value)}
            className="bg-[#0B1528]/70 border border-slate-700/60 rounded-lg px-3 py-2 text-xs outline-none [color-scheme:dark]" />
          <a data-testid="statement-download-button" href={`${API}/sales/statement?month=${stmtMonth}`}
            className="inline-flex items-center gap-1.5 text-xs border border-emerald-500/40 text-emerald-300 rounded-full px-4 py-2 hover:bg-emerald-500/10 transition-colors">
            <Download size={12} /> Download PDF
          </a>
        </div>
      )}
    </AdminLayout>
  );
}
